/**
 * Loans Table Columns Configuration
 */
import React from 'react';
import { StatusBadge } from '../common/StatusBadge';
import { RiskBadge } from '../common/RiskBadge';
import { formatCurrency } from '../../lib/formatters';
import { Badge } from '../ui/badge';
import { Clock } from 'lucide-react';

export function getLoansColumns(navigate) {
  return [
    {
      key: 'loan_number',
      label: 'Loan #',
      className: 'w-[13%]',
    },
    {
      key: 'sanction_date',
      label: 'Sanction Date',
      className: 'w-[10%]',
      render: (row) => row.sanction_date ? new Date(row.sanction_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
    },
    { key: 'borrower_name', label: 'Borrower', className: 'w-[11%]' },
    {
      key: 'product_name',
      label: 'Product',
      className: 'w-[11%]',
      render: (row) => row.product_name || '-',
    },
    {
      key: 'securities_count',
      label: 'Pledges',
      className: 'w-[8%]',
      render: (row) => (
        <Badge
          variant={row.securities_count > 0 ? 'default' : 'outline'}
          className={`cursor-pointer text-xs ${row.securities_count > 0 ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (navigate) navigate(`/security/assignments?loan_id=${row.id}`);
          }}
        >
          {row.securities_count || 0}
        </Badge>
      ),
    },
    {
      key: 'sanctioned_amount',
      label: 'Sanctioned',
      className: 'w-[12%] tabular-nums',
      render: (row) => formatCurrency(row.sanctioned_amount),
    },
    {
      key: 'total_outstanding',
      label: 'Outstanding',
      className: 'w-[14%] tabular-nums',
      render: (row) => formatCurrency(row.total_outstanding || 0),
    },
    {
      key: 'status',
      label: 'Status',
      className: 'w-[9%] text-center pr-8',
      render: (row) => (
        <div className="flex items-center justify-center gap-1">
          <StatusBadge status={row.is_frozen ? 'Frozen' : row.status} />
          {row.cooling_off_status === 'active' && (
            <Clock className="h-3 w-3 text-emerald-500 animate-pulse" data-testid={`cooling-indicator-${row.id}`} />
          )}
        </div>
      ),
    },
    {
      key: 'npa_status',
      label: 'Risk',
      className: 'w-[7%] text-center pr-8',
      render: (row) => <div className="flex justify-center"><RiskBadge status={row.npa_status} /></div>,
    },
    {
      key: 'days_past_due',
      label: 'DPD',
      className: 'w-[5%] text-center',
      render: (row) => row.days_past_due || 0,
    },
  ];
}

function toDateString(date) {
  if (date instanceof Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }
  return date;
}

function matchesSearchTerm(loan, searchTerm) {
  if (!searchTerm) return true;
  const term = searchTerm.toLowerCase();
  return (
    loan.borrower_name?.toLowerCase().includes(term) ||
    loan.loan_number?.toLowerCase().includes(term) ||
    (loan.is_creation_draft && String(loan.draft_application_id || '').includes(term))
  );
}

function matchesDateFilter(loan, dateFrom, dateTo) {
  // Creation drafts have no sanction date — always keep them visible.
  if (!loan.sanction_date) return true;
  return !(dateFrom && loan.sanction_date < toDateString(dateFrom)) &&
    !(dateTo && loan.sanction_date > toDateString(dateTo));
}

function matchesAmountFilter(loan, minAmount, maxAmount) {
  return !(minAmount && loan.sanctioned_amount < parseFloat(minAmount)) &&
    !(maxAmount && loan.sanctioned_amount > parseFloat(maxAmount));
}

export function filterLoans(loans, filters) {
  const { searchTerm, statusFilter, loanTypeFilter, dateFrom, dateTo, minAmount, maxAmount } = filters;
  
  return loans.filter((loan) => {
    return matchesSearchTerm(loan, searchTerm) &&
      (statusFilter === 'all' || loan.status === statusFilter) &&
      (loanTypeFilter === 'all' || String(loan.loan_type_id) === loanTypeFilter) &&
      matchesDateFilter(loan, dateFrom, dateTo) &&
      matchesAmountFilter(loan, minAmount, maxAmount);
  });
}

export const INITIAL_LOAN_FORM = {
  loan_application_id: '',
  loan_type_id: '',
  borrower_name: '',
  borrower_email: '',
  borrower_phone: '',
  borrower_address: '',
  borrower_id_type: 'Aadhar',
  borrower_id_number: '',
  sanctioned_amount: '',
  rate_of_interest: '',
  tenure_months: '',
  tenure_years: '',
  tenure_extra_months: '',
  repayment_start_date: '',
  remarks: '',
};
